import { useState } from "react";
import axios from "axios";
import { Loader2, Send, CheckCircle2, MessageCircle, Phone, Mail } from "lucide-react";
import { SITE_CONFIG, getWhatsAppLink, getTelLink, getMailLink } from "../lib/config";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOLOGIE = [
  "Impianto fotovoltaico residenziale",
  "Impianto fotovoltaico aziendale",
  "Conto Termico 2.0 / 3.0",
  "Pratica GSE / E-Distribuzione",
  "Progettazione elettrica",
  "Comunità Energetiche (CER)",
  "Altro",
];

export default function ConsulenzaPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    tipologia: TIPOLOGIE[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Richiesta inviata! Ti contatterò al più presto.");
      setForm({
        name: "",
        email: "",
        phone: "",
        city: "",
        tipologia: TIPOLOGIE[0],
        message: "",
      });
    } catch (err) {
      toast.error("Errore durante l'invio. Prova via WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="consulenza-page" className="min-h-screen bg-slate-950 text-white">
      <section className="relative pt-40 pb-16">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 30%, rgba(16,185,129,0.2), transparent 50%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-5">
          <div className="text-xs font-semibold tracking-[0.25em] text-emerald-400 uppercase mb-4">
            Consulenza
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
            Richiedi una <span className="text-emerald-400">Consulenza</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl">
            Compila il modulo qui sotto: ricevi una risposta personalizzata entro 24 ore.
            Preferisci una chiacchierata? Scrivimi su WhatsApp.
          </p>
        </div>
      </section>

      <section data-testid="consulenza-section" className="pb-24">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-[1.4fr_1fr] gap-10">
          {/* FORM */}
          <div className="p-8 lg:p-10 rounded-3xl border border-white/5 bg-white/[0.02] backdrop-blur-sm">
            {done ? (
              <div data-testid="form-success" className="text-center py-16">
                <div className="w-20 h-20 rounded-full bg-emerald-500/20 mx-auto flex items-center justify-center mb-6">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Richiesta inviata!</h3>
                <p className="text-slate-400 mb-6">
                  Grazie per averci scritto. Ti contatterò entro 24 ore al recapito indicato.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="form-reset-button"
                  className="px-6 py-2.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 font-semibold text-sm hover:bg-emerald-500/20 transition"
                >
                  Invia un'altra richiesta
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="consulenza-form" className="space-y-5">
                <h2 className="text-2xl font-bold text-white mb-2">Parlami del tuo progetto</h2>
                <p className="text-sm text-slate-400 mb-6">
                  Tutti i campi con * sono obbligatori.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <FormField
                    label="Nome e cognome *"
                    testId="form-name"
                    value={form.name}
                    onChange={handle("name")}
                    placeholder="Es. Mario Rossi"
                    required
                  />
                  <FormField
                    label="Email *"
                    testId="form-email"
                    type="email"
                    value={form.email}
                    onChange={handle("email")}
                    placeholder="mario@email.it"
                    required
                  />
                  <FormField
                    label="Telefono"
                    testId="form-phone"
                    type="tel"
                    value={form.phone}
                    onChange={handle("phone")}
                    placeholder="+39 ..."
                  />
                  <FormField
                    label="Città"
                    testId="form-city"
                    value={form.city}
                    onChange={handle("city")}
                    placeholder="La tua città"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 font-medium mb-2 block">
                    Tipologia di servizio
                  </label>
                  <select
                    data-testid="form-tipologia"
                    value={form.tipologia}
                    onChange={handle("tipologia")}
                    className="w-full px-4 py-3 rounded-2xl bg-slate-900/60 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-400/50 focus:bg-slate-900/80 transition"
                  >
                    {TIPOLOGIE.map((t) => (
                      <option key={t} value={t} className="bg-slate-900">
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-300 font-medium mb-2 block">
                    Messaggio *
                  </label>
                  <textarea
                    data-testid="form-message"
                    value={form.message}
                    onChange={handle("message")}
                    required
                    rows={5}
                    placeholder="Descrivi brevemente le tue esigenze (es. potenza desiderata, consumi annui, eventuale impianto già esistente...)"
                    className="w-full px-4 py-3 rounded-2xl bg-slate-900/60 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-400/50 focus:bg-slate-900/80 transition placeholder:text-slate-500"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="form-submit-button"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 font-semibold text-base hover:shadow-2xl hover:shadow-emerald-500/40 hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100 transition-all"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Invia la richiesta
                    </>
                  )}
                </button>
                <p className="text-xs text-slate-500 text-center">
                  Inviando questa richiesta accetti il trattamento dei dati ai sensi del GDPR.
                </p>
              </form>
            )}
          </div>

          {/* CONTACT SIDE */}
          <div className="space-y-4">
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="side-whatsapp"
              className="block p-6 rounded-3xl bg-gradient-to-br from-emerald-500/20 to-teal-500/10 border border-emerald-400/30 hover:border-emerald-400/60 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#25D366] flex items-center justify-center shrink-0">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-xs font-semibold tracking-wider text-emerald-300 uppercase">
                    WhatsApp — Risposta rapida
                  </div>
                  <div className="text-base font-bold text-white">{SITE_CONFIG.phone}</div>
                </div>
              </div>
              <p className="text-sm text-slate-300 mt-4">
                Scrivimi ora: risposta in poche ore in giornate lavorative.
              </p>
            </a>

            <a
              href={getTelLink()}
              data-testid="side-tel"
              className="block p-6 rounded-3xl bg-white/[0.02] border border-white/5 hover:border-emerald-400/30 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <Phone className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <div className="text-xs font-semibold tracking-wider text-emerald-300 uppercase">
                    Telefono
                  </div>
                  <div className="text-base font-bold text-white">{SITE_CONFIG.phone}</div>
                </div>
              </div>
            </a>

            <a
              href={getMailLink("Richiesta consulenza dal sito")}
              data-testid="side-email"
              className="block p-6 rounded-3xl bg-white/[0.02] border border-white/5 hover:border-emerald-400/30 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <Mail className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <div className="text-xs font-semibold tracking-wider text-emerald-300 uppercase">
                    Email
                  </div>
                  <div className="text-sm font-bold text-white break-all">{SITE_CONFIG.email}</div>
                </div>
              </div>
            </a>

            <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/5">
              <div className="text-xs font-semibold tracking-wider text-emerald-300 uppercase mb-2">
                Orari
              </div>
              <ul className="text-sm text-slate-300 space-y-1">
                <li>Lun — Ven: 09:00 — 19:00</li>
                <li>Sabato: 09:00 — 13:00</li>
                <li>Domenica: chiuso</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const FormField = ({ label, testId, ...props }) => (
  <div>
    <label className="text-sm text-slate-300 font-medium mb-2 block">{label}</label>
    <input
      data-testid={testId}
      {...props}
      className="w-full px-4 py-3 rounded-2xl bg-slate-900/60 border border-white/10 text-white text-sm focus:outline-none focus:border-emerald-400/50 focus:bg-slate-900/80 transition placeholder:text-slate-500"
    />
  </div>
);
